import type { Translation } from "../i18n-types.js"

const fr = {
  common: {
    countViews: "{{aucune vue|1 vue|?? vues}}",
    countReactions: "{{aucune réaction|1 réaction|?? réactions}}",
    back: "Retour",
    blog: "Articles",
    settings: "Paramètres",
    addPosts: "créer un article",
    save: "Enregistrer",
    filter: "Filtrer",
    tag: "Tag",
    sort: "Trier",
    all: "Tout",
    loading: "Chargement...",
    saving: "Enregistrement...",
    delete: "Supprimer",
    edit: "Modifier",
    close: "Fermer",
    error: "Erreur lors de l'enregistrement",
    networkError: "Erreur lors de l'enregistrement. Veuillez vérifier votre connexion",
    validationError: "Veuillez vérifier le formulaire",
    by: "par",
    cancel: "Annuler",
    private: "Privé",
    holdToConfirm: "Tenir pour confirmer",
    done: "Fait !",
    defaultLanguage: "par défaut",
    translating: "Traduction en cours...",
    translateAll: "Tout traduire",
    translateFrom: "Traduire depuis {language}",
  },
  mainLayout: {
    errorLoadingData: "Une erreur s'est produite lors du chargement des données de cette page. Veuillez réessayer.",
    reloadPage: "Recharger la page",
  },
  dashboard: {
    welcomeBack: "Bon retour",
    latestPosts: "Derniers articles",
    viewAllPosts: "Voir tous les articles",
    noPostsYet: "Vous n'avez encore publié aucun article.",
    latestDrafts: "Derniers brouillons",
    viewAllDrafts: "Voir tout",
    noDrafts: "Vous n'avez aucun brouillon",
    latestNotes: "Dernières notes",
    noNotesYet: "Vous n'avez encore aucune note",
    subscribers: "Abonnés",
    posts: "Articles",
    views: "Vues",
    openRate: "Taux d'ouverture",
    published: "Publié",
    created: "Créé",
    recentReactions: "Réactions récentes",
    noReactionsYet: "Vous n'avez pas encore de réactions",
    reactedToYourPost: "ont réagi à votre publication",
    othersCount: "{{aucune autre|1 autre personne|?? autres personnes}}",
    createPublication: {
      title: "Créez votre propre publication",
      description: "Commencez votre propre coin sur le web. partagez vos pensées, construisez votre audience et possédez votre contenu.",
      button: "Commencer",
    },
    verifyEmail: {
      title: "Vérifiez votre e-mail",
      description: "Vérifiez votre adresse e-mail pour débloquer toutes les fonctionnalités, comme la création de votre propre publication.",
      button: "Vérifier l'e-mail",
    },
  },
  settings: {
    title: "Paramètres",
    localizationTitle: "Langue",
    localizationDescription: "Choisissez votre langue préférée",
    interfaceLanguageLabel: "Langue de l'interface",
    authenticationTitle: "Authentification",
    authenticationDescription: "Gérer votre compte",
    signedInAs: "Connecté en tant que {email}",
    signOut: "Se déconnecter",
    signingOut: "Déconnexion...",
    authorProfileTitle: "Profil d'auteur",
    authorProfileDescription: "Informations vous concernant qui seront affichées sur votre publication",
    profileImageLabel: "Photo de profil (120×120)",
    authorNameLabel: "Nom d'auteur",
    authorNamePlaceholder: "Votre nom",
    bioLabel: "Bio",
    bioPlaceholder: "Parlez de vous à vos lecteurs",
    imageSaved: "Image téléversée",
    imageDeleted: "Image supprimée",
    informationSaved: "Enregistré avec succès",
    social: {
      title: "réseaux sociaux",
      description: "connectez vos profils sociaux à votre publication",
      social_x: {
        label: "X (Twitter)",
        placeholder: "https://x.com/nomutilisateur",
      },
      social_instagram: {
        label: "Instagram",
        placeholder: "https://instagram.com/nomutilisateur",
      },
      social_tiktok: {
        label: "TikTok",
        placeholder: "https://tiktok.com/@nomutilisateur",
      },
      social_youtube: {
        label: "YouTube",
        placeholder: "https://youtube.com/@nomutilisateur",
      },
      social_facebook: {
        label: "Facebook",
        placeholder: "https://facebook.com/nomutilisateur",
      },
      social_linkedin: {
        label: "LinkedIn",
        placeholder: "https://linkedin.com/in/nomutilisateur",
      },
      social_bluesky: {
        label: "Bluesky",
        placeholder: "https://bsky.app/profile/nomutilisateur",
      },
      social_books: {
        label: "Livres",
        placeholder: "https://goodreads.com/user/show/nomutilisateur",
      },
      social_website: {
        label: "Site web",
        placeholder: "https://votresite.com",
      },
      social_soundcloud: {
        label: "SoundCloud",
        placeholder: "https://soundcloud.com/nomutilisateur",
      },
      social_bandcamp: {
        label: "Bandcamp",
        placeholder: "https://nomutilisateur.bandcamp.com",
      },
    },
    subscriptions: {
      empty: "Vous n'avez pas encore d'abonnements",
      unsubscribeTitle: "Se désabonner",
      unsubscribeDescription: "Vous ne recevrez plus de mises à jour de cette publication.",
      unsubscribePaidDisclaimer: "Votre plan payant actuel sera également annulé.",
      holdToUnsubscribe: "Tenir pour se désabonner",
      unsubscribing: "Désabonnement...",
      unsubscribed: "Désabonné",
    },
    deleteAccount: {
      title: "Supprimer le compte",
      description: "Supprimer définitivement votre compte et toutes les données",
      warning: "Cette action ne peut pas être annulée. Tous les articles, notes et données d'abonnés seront définitivement supprimés.",
      button: "Supprimer le compte",
      confirmTitle: "Supprimer votre compte ?",
      confirmDescription: "Cela supprimera définitivement votre compte et tout le contenu. Cette action ne peut pas être annulée.",
      exportBeforeDeletePrefix: "Avant de supprimer, vous pouvez",
      exportBeforeDeleteSuffix: "vos données de publication, y compris tous les articles, notes et informations d'abonnés.",
      typeToConfirm: "Tapez {text} pour confirmer",
      confirmButton: "Supprimer mon compte",
    },
  },
  post: {
    preview: "aperçu",
    previewDescription: "Voyez comment votre article apparaîtrait pour vos lecteurs.",
    unarchive: "désarchiver",
    update: "mettre à jour",
    updated: "Publication mise à jour avec succès",
    publish: "Continuer",
    uploadHeaderImage: "Téléverser des images d'en-tête (16:9, 800 × 450 px)",
    title: "Titre",
    subtitle: "Sous-titre",
    statusSpam: "Cette version est marquée comme spam. Contactez le support si vous pensez qu'il s'agit d'une erreur.",
    statusArchived: "Cette version est archivée, elle n'est pas visible, mais vous pouvez toujours l'éditer.",
    statusSent: "Cette version a été envoyée à vos abonnés e-mail",
    statusPublished: "Cette version est publiée et visible par vos lecteurs",
    statusDraft: "Ceci est un brouillon : vous pouvez en voir l'aperçu, mais il n'est pas visible par vos lecteurs",
    words: "mots",
    savedAt: "Enregistré à {time}",
    notes: "Notes",
    notesDescription: "Créez et épinglez des notes à cet article.",
    proofread: "Relecture",
    proofreadDescription: "Obtenez de l'aide pour améliorer la clarté et l'engagement de votre article.",
    versions: "Versions",
    settings: "Paramètres",
    settingsDescription: "Configurez votre publication.",
    translations: "Traductions",
    alsoIn: "Aussi en",
    translationsDescription: "Traduisez automatiquement votre article dans d'autres langues. Touchez plus de lecteurs.",
    share: "Partager",
    shareDescription: "Obtenez un lien pour partager votre article sur les réseaux sociaux.",
    focusMode: "Mode concentration",
    focusModeDescription: "Transformez l'éditeur en un mode sans distraction pour vous concentrer sur votre écriture.",
    translationOutdated: "Cette traduction a été faite pour une version précédente",
    mainLanguage: "Langue principale",
    alternativeLanguages: "Langues alternatives",
    alternativeLanguagesDescription: "Activez les traductions pour votre article. Activez les langues pour les générer ou désactivez-les pour les supprimer.",
    translationsSaved: "Traductions enregistrées avec succès",
    translationOutdatedMessage: "Cette traduction est obsolète.",
    translationUpToDate: "Cette traduction est à jour.",
    regenerateTranslation: "La mettre à jour",
    translationOutdatedOr: "Ou",
    markTranslationAsUpdated: "Marquer comme à jour",
    updatingTranslation: "Mise à jour de la traduction...",
    translationRegenerated: "Traduction mise à jour avec succès",
    saveChanges: "Enregistrer les modifications",
    saveAndGenerate: "Enregistrer et générer",
    saveAndDelete: "Enregistrer et supprimer",
    publishPage: {
      update: "Mettre À Jour",
      publish: "Publier",
      delivery: "Livraison",
      deliveryDescription: "Comment voulez-vous livrer cette publication ?",
      sendViaEmail: "Envoyer par e-mail",
      alreadySent: "Vous avez déjà envoyé cette publication à vos abonnés par e-mail le {date}",
      subscribersWillReceive: "{{Aucun abonné|1 abonné|?? abonnés}} recevront cette publication dans leur boîte de réception.",
      sendTestToMyself: "M'envoyer un test",
      testEmailSent: "E-mail de test envoyé avec succès",
      testEmailError: "Échec de l'envoi de l'e-mail de test",
      audienceTitle: "Audience",
      audienceDescription: "Choisissez qui peut voir cet article.",
    },
  },
  editor: {
    defaultPlaceholder: "Écrivez quelque chose…",
    highlightColor: "Couleur de surlignage",
    noHighlight: "Aucun surlignage",
    grey: "Gris",
    yellow: "Jaune",
    blue: "Bleu",
    green: "Vert",
    orange: "Orange",
    red: "Rouge",
    bulletList: "Liste à puces",
    orderedList: "Liste numérotée",
    image: "Image",
    code: "Code",
    quote: "Citation",
    subscribe: "S'abonner",
    imageTitlePlaceholder: "Titre de l'image",
    imageDescriptionPlaceholder: "Description pour l'accessibilité",
    imageSizeSmall: "Petit",
    imageSizeMedium: "Moyen",
    imageSizeBig: "Pleine largeur",
    turnIntoLabel: "Transformer en...",
    heading1: "Titre 1",
    heading2: "Titre 2",
    heading3: "Titre 3",
    blockquote: "Citation",
    text: "Texte",
    callout: "Encadré",
    bold: "Gras",
    italic: "Italique",
    strikethrough: "Barré",
    inlineCode: "Code en ligne",
    highlight: "Surlignage",
    link: "Lien",
    horizontalRule: "Séparateur",
    undo: "Annuler",
    redo: "Rétablir",
    insertBlock: "Insérer un bloc",
    more: "Plus",
    snippet: "Snippet",
    codeBlock: "Bloc de code",
    math: "Math",
    embed: "Embed",
    embedDisplay: "Affichage",
    embedDisplayEmbed: "Interactif",
    embedDisplayCard: "Carte",
    language: "Langue",
  },
  notes: {
    newNote: "Nouvelle note",
    cancel: "annuler",
    noPinnedYet: "aucune note",
    voice: "voix",
    copyToPost: "copier dans l'article",
  },
  blog: {
    title: "Publication",
    audience: "Audience",
    forms: "Forms",
    stats: "Statistiques",
    noResultsFound: "Aucun résultat",
    noPostsYet: "Vous n'avez encore créé aucun article.",
    filter: {
      spam: "Spam",
      archived: "Archivé",
      sent: "Envoyé",
      published: "Publié",
      draft: "Brouillon",
    },
    blogName: "Nom de la publication",
    blogNameDescription: "Définissez le nom de votre publication : il sera utilisé pour l'identifier et générer l'URL",
    atLeast3Characters: "3 à 15 caractères : lettres, chiffres ou tirets.",
    nameAvailable: "Ce nom est disponible.",
    nameInvalid: "Ce nom est invalide.",
    nameUnavailable: "Ce nom est déjà pris.",
    nameNaughty: "Ce nom n'est pas autorisé.",
    nameChangeCooldown: "Vous pourrez changer votre nom dans {days} {{jour|jours}}.",
    nameChangeWarning: "Après le changement, vous ne pourrez pas modifier votre nom pendant 30 jours. Votre ancienne adresse redirigera vers la nouvelle.",
    checkingNameAvailability: "Vérification de la disponibilité du nom...",
    language: "Langue",
    languageDescription: "Définissez la langue de votre publication",
    additionalLanguages: "Langues supplémentaires",
    additionalLanguagesDescription: "Choisissez quelles langues sont disponibles pour les traductions de votre publication. Toutes les langues sont activées par défaut.",
    titleAndDescription: "Titre et description",
    titleAndDescriptionDescription: "Définissez le titre et la description de votre espace",
    yourBlogTitle: "titre-de-votre-publication",
    description: "Description",
    yourBlogDescription: "description-de-votre-publication",
    branding: "Branding",
    brandingDescription: "Ajoutez votre propre identité visuelle à votre publication et vos e-mails",
    logo: "Logo",
    logoDescription: "Affiché en haut de votre publication et dans les e-mails. Taille recommandée : 200 × 200 px.",
    uploadLogo: "Téléverser un logo",
    wordmark: "Logotype",
    wordmarkDescription: "Affiché à la place du nom de la publication en haut du site. Taille recommandée : 1000 × 200 px.",
    uploadWordmark: "Téléverser un logotype",
    subscribeImage: "Image d'abonnement",
    subscribeImageDescription: "Affichée dans le formulaire d'abonnement. Taille recommandée : 600 × 600 px.",
    uploadSubscribeImage: "Téléverser l'image",
    favicon: "Favicon",
    faviconDescription: "Affiché comme icône d'onglet du navigateur pour votre publication. Taille recommandée : 128 × 128 px.",
    uploadFavicon: "Téléverser le favicon",
    header: "En-tête",
    headerDescription: "Ajoutez un en-tête personnalisé à chacun de vos articles",
    footer: "Pied de page",
    footerDescription: "Ajoutez un pied de page personnalisé à chacun de vos articles",
    welcomeEmail: {
      title: "E-mail de bienvenue",
      description: "Personnalisez l'e-mail de bienvenue envoyé aux nouveaux abonnés",
      subjectLabel: "Objet de l'e-mail",
      subjectContent: "Contenu de l'e-mail",
    },
    export: {
      title: "Exporter",
      description: "Exportez tous les abonnés et le contenu de votre publication",
      generateExport: "Générer l'export",
      generating: "Génération...",
      exportedAt: "Exporté le",
      subscribers: "Abonnés",
      posts: "Publications",
      download: "Télécharger",
      processing: "En cours...",
      error: "Erreur",
      noExports: "Pas encore d'exports",
    },
    subscribersPage: {
      email: "Email",
      subscribedAt: "Abonné le",
      tier: "Niveau",
      form: "Form",
      allForms: "All forms",
      direct: "Direct",
      free: "Gratuit",
      allTiers: "Tous les niveaux",
      noSubscribersFound: "Aucun abonné trouvé",
      noSubscribersYet: "Pas encore d'abonnés",
      publication: "Publication",
      followBack: "S'abonner",
      following: "Abonné",
    },
    formsPage: {
      editorTitle: "Create form",
      editEditorTitle: "Edit form",
      createForm: "Create form",
      editForm: "Edit form",
      name: "Name",
      audience: "Destinataires",
      audienceDescription: "Choisissez qui peut recevoir l'e-mail contenant les fichiers. Si l'accès est restreint, il faudra d'abord s'abonner ou passer à un niveau supérieur.",
      downloads: "Files",
      subscribers: "Subscribers",
      size: "Size",
      actions: "Actions",
      everyone: "Tout le monde",
      everyoneDescription: "Tout le monde peut envoyer le formulaire et recevoir l'e-mail contenant les fichiers.",
      subscribersOnly: "Abonnés uniquement",
      subscribersOnlyDescription: "Seuls les abonnés peuvent recevoir l'e-mail contenant les fichiers.",
      content: "Description",
      email: "Email",
      emailSubject: "Email subject",
      emailBody: "Email body",
      formSectionTitle: "Create form",
      formSectionDescription: "Set up your form and file delivery",
      filesSectionTitle: "Files",
      filesSectionDescription: "Upload the files readers will receive",
      emailSectionDescription: "Customize the delivery email sent after submit",
      defaultDescription: "Abonnez-vous pour recevoir ces fichiers par e-mail.",
      defaultEmailSubject: "Vos fichiers sont prets",
      defaultEmailSubjectWithName: "{publicationName}: vos fichiers sont prets",
      defaultEmailGreeting: "Bonjour,",
      defaultEmailIntro: "merci pour votre inscription. Vos liens de telechargement sont ci-dessous.",
      defaultEmailOutro: "Si vous avez la moindre question, repondez simplement a cet e-mail.",
      defaultEmailSignature: "- L'equipe",
      defaultEmailSignatureWithName: "- {publicationName}",
      dropFiles: "Drop the files",
      formCreated: "Form created",
      formUpdated: "Form updated",
      formDeleted: "Form deleted",
      blankSlate: {
        title: "Capture, nurture, convert",
        growReadership: "Grow your readership",
        growReadershipDescription: "Capture more readers by offering exclusive content behind a subscription form.",
        convertReaders: "Convert readers into paid subscribers",
        convertReadersDescription: "Offer useful downloads and resources that turn readers into subscribers.",
        goBeyondText: "Go beyond text",
        goBeyondTextDescription: "Give your audience more by supplementing your written content with downloadable media and resources.",
      },
    },
    reactionEmojis: "Emojis de réaction",
    reactionEmojisDescription: "Choisissez quels emojis les lecteurs peuvent utiliser pour réagir à vos publications",
    addEmoji: "Ajouter un emoji",
    interval: "intervalle",
    referrer: "référent",
    visitors: "visiteurs",
    views: "vues",
    country: "pays",
    channel: "canal",
  },
  categories: {
    culture: "Culture",
    technology: "Technologie",
    business: "Business",
    usPolitics: "Politique US",
    finance: "Finance",
    food: "Nourriture",
    podcast: "Podcast",
    sports: "Sports",
    art: "Art",
    worldPolitics: "Politique Mondiale",
    healthPolitics: "Politique de Santé",
    news: "Actualités",
    fashionBeauty: "Mode & Beauté",
    music: "Musique",
    faith: "Foi",
    climate: "Climat",
    science: "Science",
    literature: "Littérature",
    fiction: "Fiction",
    health: "Santé",
    design: "Design",
    travel: "Voyage",
    parenting: "Parentalité",
    philosophy: "Philosophie",
    comics: "Bandes Dessinées",
    international: "International",
    crypto: "Crypto",
    history: "Histoire",
    humor: "Humour",
    education: "Éducation",
    filmTv: "Cinéma & TV",
  },
  readerOnboarding: {
    subscribedTo: "Vous êtes abonné à \"{publicationTitle}\"",
    selectInterests: "Quels sujets vous intéressent ?",
    selectInterestsDescription: "Sélectionnez les catégories que vous aimeriez lire. Cela nous aide à vous recommander des publications que vous aimerez.",
    continue: "Continuer",
    skip: "Passer pour l'instant",
  },
  onboarding: {
    buildAudience: "Construisez votre audience à votre manière",
    ownYourContent: "Sur Fika, vous possédez ce que vous écrivez et la communauté qui se rassemble autour.",
    nameYourBlog: "Commencez par nommer votre publication ; votre petit coin du vaste web libre.",
    yourBlogNamePlaceholder: "nom-de-votre-publication",
    createBlog: "Créer la publication",
    continue: "Continuer",
    updateBlog: "Mettre à jour la publication",
    onboardingRabbitAlt: "Lapin d'intégration",
    chooseLanguage: {
      title: "Dans quelles langues écrirez-vous ?",
      description: "Choisissez la langue de votre publication et activez des langues supplémentaires pour les traductions.",
    },
    createFirstPost: {
      title: "Publiez votre premier article",
      description: "Commencez à écrire votre premier article et démarrez avec Fika. Partagez vos pensées avec le monde !",
      descriptionWithDraft: "Continuez à écrire votre premier article et démarrez avec Fika. Partagez vos pensées avec le monde !",
      ignore: "Ignorer",
      createPost: "Créer un article",
      continueWriting: "Continuer à écrire",
    },
    createSecondPost: {
      title: "Publiez votre deuxième article",
      description: "Écrivez un autre article et partagez-le avec vos lecteurs.",
      descriptionWithDraft: "Continuez à écrire votre deuxième article.",
      ignore: "Ignorer",
      createPost: "Créer un article",
      continueWriting: "Continuer à écrire",
      creating: "Création...",
    },
    fillProfile: {
      title: "Complétez votre profil",
      description: "Ajoutez votre nom, votre biographie et votre photo de profil pour personnaliser votre présence sur Fika.",
      ignore: "Ignorer",
      editProfile: "Modifier le profil",
    },
    setLanguage: {
      title: "Définissez la langue de votre publication",
      description: "Choisissez la langue principale de votre publication. Cela aide les lecteurs à trouver votre contenu.",
      ignore: "Ignorer",
      chooseLanguage: "Choisir la langue",
    },
    fillPublication: {
      title: "Ajoutez les détails de la publication",
      description: "Donnez un titre, une description et un logo à votre publication pour la faire ressortir.",
      ignore: "Ignorer",
      editDetails: "Modifier les détails",
    },
    translateArticles: {
      title: "Traduisez \"{title}\"",
      description: "Ajoutez des traductions à vos articles pour toucher des lecteurs dans différentes langues.",
      ignore: "Ignorer",
      translateLatestArticle: "Traduire l'article",
    },
  },
  postSettings: {
    title: "paramètres",
    customPagesTitle: "pages personnalisées",
    customPagesDescription: "faites apparaître cet article dans la barre de navigation",
    customPageLabel: "page personnalisée",
    seoMetadataTitle: "méta-données seo",
    seoMetadataDescription: "le titre et la description de l'article comme il apparaîtra dans les résultats de recherche",
    seoMetadataSaved: "méta-données enregistrées",
    metaTitleLabel: "métatitre",
    metaTitlePlaceholder: "titre de votre publication",
    metaDescriptionLabel: "métadescription",
    metaDescriptionPlaceholder: "description de votre publication",
    canonicalTitle: "canonique",
    canonicalDescription: "si la source originale de l'article est un autre site web, vous pouvez spécifier l'URL canonique ici pour éviter les problèmes de contenu dupliqué.",
    canonicalUrlLabel: "url canonique",
    canonicalUrlSaved: "url canonique enregistrée",
    pinSaved: "page personnalisée enregistrée",
    tagsTitle: "tags",
    tagsDescription: "ajoutez des tags à votre article",
    audienceSaved: "paramètres d'audience enregistrés",
    audienceTitle: "Audience",
    audienceDescription: "Choisissez qui peut accéder à cet article.",
    audienceEveryoneLabel: "Tous",
    audienceEveryoneDescription: "Tout le monde peut voir, y compris les visiteurs non abonnés.",
    audienceAllSubscribersLabel: "Abonnés uniquement",
    audienceAllSubscribersDescription: "Tout abonné peut voir cet article.",
    commentsEnabledTitle: "Comments",
    commentsEnabledDescription: "Allow readers to comment on this post.",
    publishedAtTitle: "date de publication",
    publishedAtDescription: "modifier la date de première publication de cet article.",
    publishedAtLabel: "date de publication",
    publishedAtSaved: "date de publication enregistrée",
    archivePostTitle: "archiver l'article",
    unarchivePostTitle: "désarchiver l'article",
    archivePostDescription: "masquer cet article de votre publication.",
    unarchivePostDescription: "afficher cet article sur votre publication.",
    archivePostSaved: "article archivé",
    deletePostTitle: "supprimer l'article",
    deletePostDescription: "supprimer cet article définitivement.",
    deletePostWarning: "cette action est irréversible.",
    deletePostSaved: "article supprimé",
    holdToDelete: "Tenir pour supprimer",
    deleting: "Suppression...",
    deleted: "Supprimé !",
  },
  postShare: {
    preview: "Aperçu",
    previewDescription: "Comment votre publication apparaîtra sur les réseaux sociaux et dans les résultats de recherche",
    articleLink: "Lien de l'article",
    copyLink: "Copier le lien",
    linkCopied: "Lien copié dans le presse-papiers",
    metaTitleLabel: "Méta titre",
    metaTitlePlaceholder: "Le titre de votre publication",
    metaDescriptionLabel: "Méta description",
    metaDescriptionPlaceholder: "La description de votre publication",
    shareOnSocialMedia: "Partager sur les réseaux sociaux",
    shareOn: "Partager sur {network}",
    publishedCongrats: "Votre publication est en ligne !",
    shareNow: "Partagez-la maintenant avec le monde ! 🚀",
    backToPost: "Retour à la publication",
    setCoverImage: "Définir comme couverture",
    resetCoverImage: "Réinitialiser à l'original",
    coverImageUpdated: "Image de couverture mise à jour",
    socialImages: "Images pour les réseaux sociaux",
    socialImagesDescription: "Téléchargez des images pour les réseaux sociaux",
    download: "Télécharger",
  },
  postVersions: {
    title: "versions",
    loading: "chargement des versions...",
    failed: "échec du chargement des versions",
    failedVersion: "échec du chargement de la version",
    restored: "version restaurée avec succès",
    restore: "restaurer",
    language: "langue",
    pills: {
      current: "actuelle",
      archived: "archivée",
      sent: "envoyée",
      published: "publiée",
    },
  },
  subscribe: {
    defaultText: "Abonnez-vous à {userName} pour recevoir les mises à jour directement dans votre boîte de réception",
    button: "S'abonner",
    emailPlaceholder: "Saisissez votre e-mail",
  },
  snippet: {
    placeholder: "Décrivez ce que vous voulez créer...",
    placeholderUpdate: "Décrivez ce que vous voulez modifier...",
    buttonCreate: "créer",
    buttonUpdate: "mettre à jour",
    buttonCreating: "Création...",
    buttonUpdating: "Mise à jour...",
  },
  auth: {
    signInOrSignUp: "Se connecter",
    registrationsClosed: "Les inscriptions sont fermées. Les utilisateurs existants peuvent se connecter.",
    accountSuspendedTitle: "Compte suspendu",
    accountSuspendedMessage: "Votre compte a été suspendu. Contactez humans@fika.bar si vous pensez qu'il s'agit d'une erreur.",
    continueWithGoogle: "Continuer avec Google",
    continueWithLinkedIn: "Continuer avec LinkedIn",
    signingIn: "Connexion...",
    or: "ou",
    enterYourEmail: "Saisissez votre e-mail",
    failedToSend: "Échec de l'envoi du code de vérification. Veuillez réessayer.",
    sending: "Envoi...",
    enterCode: "Veuillez entrer le code à 6 chiffres envoyé à",
    verifying: "Vérification...",
    invalidCode: "Code invalide ou expiré. Veuillez réessayer.",
    useDifferentEmail: "Utiliser une autre adresse e-mail",
    subscribeTo: "S'abonner à",
    thisBlog: "cette publication",
    getUpdates: "Abonnez-vous pour recevoir les mises à jour dans votre boîte de réception",
    subscribe: "S'abonner",
    somethingWentWrong: "Une erreur s'est produite",
    disclaimerPrefix: "En envoyant, vous acceptez la",
    disclaimerPrivacy: "politique de confidentialité",
    disclaimerAnd: "et les",
    disclaimerTerms: "conditions d'utilisation",
    tierSelectionTitleSubscribed: "Vous êtes abonné à {publicationName} !",
    tierSelectionTitleSubscribedNoName: "Vous êtes abonné !",
    subscriptionError: "Une erreur s'est produite. Veuillez réessayer.",
    verifyEmailSuccess: "E-mail vérifié avec succès !",
    emailNotVerified: "Veuillez vérifier votre e-mail pour effectuer cette action",
    subscriptionConfirmationDescriptionBeforeAuthor: "Vous recevrez les mises à jour de ",
    subscriptionConfirmationDescriptionAfterAuthor: " directement dans votre boîte de réception.",
    subscriptionConfirmationDescriptionNoAuthor: "Vous recevrez les mises à jour directement dans votre boîte de réception.",
  },
  unsubscribe: {
    writerSuccess: "vous êtes désabonné",
    writerSuccessDescription: "vous ne recevrez plus d'e-mails de cet auteur. vous pouvez toujours vous réabonner depuis son profil.",
    marketingSuccess: "vous êtes désabonné",
    marketingSuccessDescription: "vous ne recevrez plus d'e-mails promotionnels de fika. vous pouvez gérer vos préférences dans les paramètres.",
    weeklyFeaturedSuccess: "vous avez été désabonné",
    weeklyFeaturedSuccessDescription: "vous ne recevrez plus l'e-mail hebdomadaire des articles mis en avant. vous pouvez le réactiver dans les paramètres.",
    error: "une erreur s'est produite",
    invalidToken: "ce lien de désabonnement est invalide ou a expiré. veuillez réessayer depuis un e-mail récent.",
    missingToken: "aucun jeton de désabonnement n'a été fourni. veuillez utiliser le lien de votre e-mail.",
    genericError: "une erreur inattendue s'est produite. veuillez réessayer plus tard.",
    invalidRequest: "demande invalide",
    invalidRequestDescription: "cette page nécessite un lien de désabonnement valide provenant d'un e-mail.",
    signInToManage: "connectez-vous pour gérer les abonnements",
  },
  subscriptions: {
    title: "Abonnements",
    manage: "Gérer",
    manageTitle: "Gérer les abonnements",
    viewOriginal: "Voir l'original",
    noPublishedPostsYet: "Aucune publication publiée pour le moment",
    previousPost: "Article précédent",
    nextPost: "Article suivant",
    following: "Abonné",
    subscribed: "Abonné",
    renewsAt: "Se renouvelle le {date}",
    upgradePlan: "Passer au premium",
    tierChangeError: "Échec du changement de plan",
    manageBilling: "Facturation et reçus",
    subscribersOnly: "Abonnés uniquement",
    tableOfContents: "Table des matières",
  },
  discover: {
    readMore: "Lire la suite",
    upgradeToRead: "Passer au plan supérieur",
    humanContent: "Humain",
    humanContentTitle: "Contenu humain",
    humanContentDescription: "Fika est très confiant que ce contenu a été écrit par un humain avec peu ou pas d'assistance de l'IA.",
  },
  privatePost: {
    subscribeCta: "S'abonner",
    upgradeCta: "Mettre à niveau pour débloquer",
  },
  applicationLayout: {
    loadingPhrases: {
      synchronizing: "synchronisation...",
      hadronCollider: "assemblage du collisionneur de hadrons...",
      reticulatingSplines: "réticulation des splines...",
      wakingHamsters: "réveil des hamsters...",
      consultingOracle: "consultation de l'oracle...",
      aligningStars: "alignement des étoiles...",
      fluxCapacitor: "calibrage du condensateur de flux...",
      traveling88mph: "voyage à 88 mph...",
      whiteRabbit: "suivi du lapin blanc...",
      reversingPolarity: "inversion de la polarité...",
      enhancingImage: "amélioration de l'image...",
      enteringMatrix: "entrée dans la matrice...",
      findingRing: "recherche de l'anneau unique...",
      dividingByZero: "division par zéro...",
    },
  },
  proofreader: {
    title: "Correcteur",
    editorTitle: "Éditeur",
    editorDescription: "Relisez votre article pour améliorer la clarté et l'impact.",
    principles: {
      grammar: {
        label: "Grammaire",
        description: "Corriger les fautes d'orthographe et de grammaire.",
        action: "Corriger la grammaire",
      },
      tighten: {
        label: "Resserrer",
        description: "Rendre le texte plus concis.",
        action: "Resserrer le texte",
      },
      refine: {
        label: "Affiner",
        description: "Améliorer la clarté, le flux et l'impact du texte.",
        action: "Affiner le texte",
      },
    },
    noFixesFound: "Aucune correction trouvée",
    countFixes: "{{Aucune correction|1 correction|?? corrections}}",
    countFixesFound: "{{Aucune correction trouvée|1 correction trouvée|?? corrections trouvées}}",
    scanning: "Analyse en cours...",
    percentComplete: "{percent}% terminé",
    discard: "Annuler",
    acceptAll: "Tout accepter",
    lenses: {
      title: "Lentilles",
      description: "Visualisez la structure des phrases pour améliorer le rythme et la lisibilité.",
      off: "désactivé",
      difficulty: "difficulté",
      difficultyDescription: "Montre la difficulté de vos phrases. Les phrases plus difficiles apparaissent en couleurs plus chaudes. Pour améliorer, utilisez des phrases plus courtes et des mots plus simples.",
      variation: "variation",
      variationDescription: "Montre la variation des longueurs de vos phrases. Les phrases monotones apparaissent en couleurs plus chaudes. Pour améliorer, utilisez un mélange de phrases courtes et longues.",
      legend: {
        difficulty: {
          orange: "Très difficile à lire",
          amber: "Difficile à lire",
        },
        variation: {
          orange: "Très monotone",
          amber: "Monotone",
        },
      },
      tips: {
        veryHard: "Cette phrase est très difficile à lire. Essayez de la diviser en phrases plus courtes ou d'utiliser des mots plus simples.",
        hard: "Cette phrase est difficile à lire. Envisagez de la simplifier.",
        veryMonotonous: "Vos phrases sont très similaires en longueur. Variez le rythme en mélangeant des phrases courtes et longues.",
        monotonous: "Cette phrase est similaire aux phrases voisines. Essayez de varier la longueur pour améliorer le flux.",
      },
    },
  },
  inbox: {
    title: "Boîte de réception",
    message: "Message",
    empty: "Pas encore de lettres ou de commentaires",
    emptyLetters: "Pas encore de lettres",
    emptyComments: "Pas encore de commentaires",
    emptyDescription: "Utilisez cet espace pour voir les lettres de vos lecteurs et les conversations avec les auteurs auxquels vous êtes abonné.",
    unread: "Non lus",
    read: "Lus",
    inReplyTo: "En réponse à :",
    writeMessage: "Écrire un message...",
    submit: "Envoyer",
    publishing: "Publication en cours...",
    you: "Vous",
    tabAll: "Tout",
    tabEmails: "Lettres",
    tabComments: "Commentaires",
    typePrivateEmail: "Lettre privée",
    typePublicComment: "Commentaire public",
    andMore: "et {count} de plus",
  },
  comments: {
    title: "Commentaires",
    writeComment: "Écrire un commentaire...",
    submit: "Publier le commentaire",
    publishing: "Publication en cours...",
    reply: "Répondre",
    delete: "Supprimer",
    deleting: "Suppression...",
    deleteConfirm: "Supprimer ce commentaire ?",
    empty: "Pas encore de commentaires. Soyez le premier à commenter !",
    anonymousParticipant: "Un abonné",
    flaggedMessage: "Votre message a été signalé comme spam ou contenu inapproprié et n'a pas pu être envoyé.",
    author: "Auteur",
    privateVisibility: "Visible uniquement pour toi et les participants",
    sendAsPrivate: "Envoyer comme lettre privée",
    translateTo: "Traduire en {locale}",
    showOriginal: "Afficher l'original ({locale})",
  },
} satisfies Translation

export default fr
